// apps/web/src/components/CollaboratorsPanel.js
import React from 'react';

export default function CollaboratorsPanel({
  list,
  collaboratorsData,
  inviteEmails,
  setInviteEmails,
  invitePermissionLevel,
  setInvitePermissionLevel,
  onSendInvites,
  inviteTokenInput,
  setInviteTokenInput,
  onAcceptInvite,
  onRefreshCollaborators,
  onRevokeInvite,
  onRemoveCollaborator,
  currentUserId,
  isAuthed,
}) {
  const { collaborators = [], pending_invites = [] } = collaboratorsData || {};

  // Map raw permission levels to display labels
  function permissionBadge(level) {
    if (!level) return null;
    const lower = String(level).toLowerCase();
    if (lower === 'edit') return 'Role: Editor';
    if (lower === 'view') return 'Role: Viewer';
    return `Role: ${level}`;
  }

  return (
    <aside className="RightPanel" aria-label="Collaboration panel">
      <div className="RightSection">
        <div className="RightSectionHeader">
          <h2>Collaborators</h2>
          <button
            className="Btn tiny"
            disabled={!isAuthed || !list}
            onClick={() => onRefreshCollaborators(list?.id)}
            title="Refresh collaborators"
            aria-label="Refresh collaborators list"
          >
            ↻
          </button>
        </div>

        {!isAuthed ? (
          <div className="RightHint">Log in to view collaborators.</div>
        ) : !list ? (
          <div className="RightHint">Select a list to see collaborators.</div>
        ) : (
          <>
            <div className="RightSubHeader">Has access now</div>
            <ul className="RightList">
              {collaborators.length === 0 ? (
                <li className="RightListEmpty">(none yet)</li>
              ) : (
                collaborators.map((c, i) => {
                  const isOwner = c.is_owner;
                  const canRemove = !isOwner && onRemoveCollaborator;
                  return (
                    <li className="RightListItem" key={c.user_id || c.id || i}>
                      <div className="CollabName">
                        {c.name || c.full_name || c.username || c.email || 'Unknown'}
                      </div>
                      <div className="CollabDetails">{c.email}</div>
                      <div className="CollabTagRow">
                        {c.is_owner ? <span className="CollabTag owner">Role: Owner</span> : null}
                        {c.permission_level ? (
                          <span className="CollabTag">{permissionBadge(c.permission_level)}</span>
                        ) : null}
                        {canRemove && (
                          <button
                            className="Btn tiny danger"
                            onClick={() => onRemoveCollaborator(list.id, c.id)}
                            title="Remove collaborator"
                            aria-label={`Remove collaborator ${c.name || c.full_name || c.username || c.email || 'Unknown'}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="RightSubHeader">Pending invites</div>
            <ul className="RightList">
              {pending_invites.length === 0 ? (
                <li className="RightListEmpty">(none pending)</li>
              ) : (
                pending_invites.map((p, i) => (
                  <li className="RightListItem pending" key={p.id || i}>
                    <div className="CollabName">{p.email}</div>
                    <div className="CollabDetails">waiting to join…</div>
                    <div className="CollabTagRow">
                      {p.permission_level ? (
                        <span className="CollabTag">{permissionBadge(p.permission_level)}</span>
                      ) : null}
                      {onRevokeInvite && (
                        <button
                          className="Btn tiny danger"
                          onClick={() => onRevokeInvite(p.id)}
                          title="Revoke this invite"
                          aria-label={`Revoke invite for ${p.email}`}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </div>

      <div className="RightSection">
        <div className="RightSectionHeader">
          <h2>Invite People</h2>
        </div>

        {!isAuthed || !list ? (
          <div className="RightHint">Log in & select a list first.</div>
        ) : (
          <>
            <div className="RightFieldLabel">Emails (comma separated):</div>
            <textarea
              className="RightInput"
              placeholder="a@b.com,  c@d.com"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  onSendInvites();
                }
              }}
              aria-label="Email addresses to invite (comma separated). Press Ctrl+Enter to send."
            />

            <div className="RightFieldLabel">Permission:</div>
            <select
              className="RightSelect"
              value={invitePermissionLevel}
              onChange={(e) => setInvitePermissionLevel(e.target.value)}
              aria-label="Permission level for invited users"
            >
              <option value="view">Viewer</option>
              <option value="edit">Editor</option>
            </select>

            <button className="Btn primary fullwidth" onClick={onSendInvites} aria-label="Send invitations">
              Send Invites
            </button>
          </>
        )}
      </div>

      <div className="RightSection">
        <div className="RightSectionHeader">
          <h2>Accept Invite</h2>
        </div>

        {!isAuthed ? (
          <div className="RightHint">Log in to accept invites.</div>
        ) : (
          <>
            <div className="RightFieldLabel">Paste Invite Token:</div>
            <textarea
              className="RightInput"
              placeholder="invite token from email"
              value={inviteTokenInput}
              onChange={(e) => setInviteTokenInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  onAcceptInvite();
                }
              }}
              aria-label="Invitation token. Press Ctrl+Enter to accept."
            />
            <button className="Btn fullwidth" onClick={onAcceptInvite} aria-label="Accept invitation">
              Accept Invite
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
